import React, { useState, useEffect, useMemo } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { Box, Divider, LinearProgress, Skeleton, Typography } from '@mui/material'
import FindInPageIcon from '@mui/icons-material/FindInPage';

import { WhiteBox, WhiteBoxHeader } from 'components/AppShell'
import { SearchTextField } from 'components/CustomFields'
import { NoResultComponent } from 'components/Home/TodoList';
import HorizontalScrollNavigator from 'components/HorizontalScrollNavigator'
import Chip from 'components/Chip'
import { getTwoDigitNo, snakeCaseToSentence } from 'utils';
import { getCaseMasterPreparationList } from 'services';
import { useServices } from 'contexts';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(7.5),
    padding: theme.spacing(5, 5, 8, 5),
    borderRadius: theme.spacing(2),
    border: `2px solid ${theme.palette.neutral['clr-200']}`,
    minWidth: '428px',
    cursor: 'pointer',
    "&:hover": {
      border: `2px solid ${theme.palette.primary['clr-100']}`,
    },
    position: 'relative'
  },
  categoryTag: {
    width: '40px',
    height: '40px',
    position: 'absolute',
    right: '20px',
    top: '20px'
  },
  completionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5)
  }
}))

export const SkeletonCard = () => {
  const classes = useStyles();

  return (
    <Box className={classes.cardContainer}>
      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '4px' }} />
      <Box className={classes.categoryTag}>
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
      <Box display="flex" flexDirection="column" gap="20px">
        <Skeleton variant="text" width="70%" height={28} />
        <Box className={classes.completionContainer}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="rectangular" width="100%" height={5} sx={{ borderRadius: 4 }} />
        </Box>
      </Box>
    </Box>
  );
};

const CaseMasterCard = ({ template, onClick }) => {
  const classes = useStyles();
  const theme = useTheme();

  const progress = useMemo(() => {
    if (!template.tag) return 0;
    if (template?.totalQuestions && template.totalQuestions > 0) {
      return (template.totalAttempted / template.totalQuestions) * 100;
    }
    return 0;
  }, [template]);

  return (
    <Box className={classes.cardContainer} onClick={onClick}>
      <Chip
        content={template?.tag ? 'Case Study Prep' : 'Personalized Case prep'}
        bgColor={theme.palette.tertiary['clr-50']}
        color={theme.palette.neutral['clr-800']}
        typographyVariant='body01-bold'
        sx={{
          borderRadius: '4px',
          padding: '4px'
        }}
      />
      <Box className={classes.categoryTag}>
        <img src='https://assets.languify.in/images/caseIcon.png'  width={40} height={40}/>
      </Box>
      <Box display='flex' flexDirection='column' gap='20px'>
        <Typography variant='h3-medium' color='neutral.clr-900' style={{ textTransform: 'capitalize' }}>
          {template?.tag?.name || snakeCaseToSentence(template?.title)}
        </Typography>

        {
          template?.tag ?
            <Box className={classes.completionContainer}>
              <Typography variant='body01-bold' color='neutral.clr-700'>
                Completed: {template?.totalAttempted || 0}/{template?.totalQuestions || 0}
              </Typography>
              <Box className={classes.progressBar}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 5,
                    borderRadius: 4,
                    backgroundColor: theme.palette.neutral["clr-200"],
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.palette.secondary["clr-900"],
                    },
                  }}
                />
              </Box>
            </Box>
            :
            <Box>
              <Typography variant='body01-bold' color='neutral.clr-600'>
                Total cases: {getTwoDigitNo(template?.details?.subTopics?.length || 15)}
              </Typography>
            </Box>
        }
      </Box>
    </Box>
  )
}

const PreparationList = ({ onClick = () => { } }) => {
  const service = useServices();
  const [loading, setLoading] = useState(false);
  const [myPreparationList, setMyPreparationList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchMyPreparationList = async () => {
      try {
        setLoading(true);
        
        const response = await service.callService(getCaseMasterPreparationList);
        
        // Transform API response to match component's expected format
        if (response && Array.isArray(response)) {
          const transformedData = response.map((item) => ({
            _id: item.topic,
            title: item.topic,
            tag: {
              _id: item.topic,
              name: snakeCaseToSentence(item.topic),
              category: 'case_topic',
              topicKey: item.topic // Keep original topic key for API calls
            },
            topicKey: item.topic, // Also add at root level
            totalQuestions: item.totalQuestions || 0,
            totalAttempted: item.totalAttempted || 0,
            updatedAt: item.updatedAt
          }));
          
          setMyPreparationList(transformedData);
        }
      } catch (err) {
        console.error('Error fetching preparation list:', err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMyPreparationList();
  }, []);

  const filteredList = useMemo(() =>
    myPreparationList?.filter((template) =>
      template?.tag?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template?.title?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [myPreparationList, searchQuery]
  );

  return (
    <WhiteBox>
      <WhiteBoxHeader
        id="my-preparation-list"
        heading="My preparation"
        subheading="Your ongoing preparation for case study types & personalized business challenges"
      >
        <SearchTextField
          placeholder="Search here..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={!myPreparationList?.length}
        />
      </WhiteBoxHeader>
      <Divider />
      <Box height={280} mt={5}>
        {
          loading
            ? (
              <Box display="flex" gap={4} overflow="auto">
                {[...Array(2)].map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))}
              </Box>
            )
            :
            filteredList.length ? (
              <HorizontalScrollNavigator
                childrensLength={1} scrollWidth={800}
                showNavigator={filteredList?.length <= 2 ? false : true}
              >
                {filteredList.map((template, index) => (
                  <CaseMasterCard
                    key={index}
                    template={template}
                    onClick={() => onClick(template)}
                  />
                ))}
              </HorizontalScrollNavigator>
            ) : (
              <NoResultComponent
                description={
                  <>
                    Nothing to show here! <br />
                    Attempt any case study challenge to get started.
                  </>
                }
                image='https://assets.languify.in/images/noPreparation.png'
              />
            )
        }
      </Box>
    </WhiteBox>
  );
};

export default PreparationList;
